import React, { useCallback, useEffect, useRef, useState } from "react";
import { adminApi } from "@/admin/AdminAuthContext";
import { Loader2, Upload, Download, Users, Search, Trash2, FileSpreadsheet, Globe, Mail, Phone, Zap, Eye, Sparkles, Compass } from "lucide-react";
import { toast } from "sonner";
import ProspectDetailPanel from "@/admin/ProspectDetailPanel";

const STATUS_LABELS = {
  raw: { label: "Brut", className: "bg-white/10 text-white/70" },
  pending_validation: { label: "À valider", className: "bg-[#E52431]/15 text-[#FFB4BB]" },
  ready_to_send: { label: "Prêt à envoyer", className: "bg-emerald-500/15 text-emerald-300" },
  rejected: { label: "Rejeté", className: "bg-white/5 text-white/40 line-through" },
};

function scoreClasses(score) {
  if (score == null) return "text-white/40";
  if (score >= 70) return "text-emerald-300";
  if (score >= 40) return "text-amber-300";
  return "text-[#FFB4BB]";
}

function StatusPill({ status }) {
  const s = STATUS_LABELS[status] || { label: status, className: "bg-white/10 text-white/60" };
  return (
    <span className={`text-[10px] uppercase tracking-[0.12em] px-2 py-1 rounded-full font-semibold ${s.className}`}>
      {s.label}
    </span>
  );
}

function StatsCards({ stats }) {
  const items = [
    { key: "total", label: "Total", value: stats.total },
    { key: "raw", label: "Bruts", value: stats.raw },
    { key: "pending_validation", label: "À valider", value: stats.pending_validation },
    { key: "ready_to_send", label: "Prêts", value: stats.ready_to_send },
    { key: "rejected", label: "Rejetés", value: stats.rejected },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="prospects-stats">
      {items.map((it) => (
        <div key={it.key} className="p-3 rounded-xl border border-white/10 bg-white/[0.02]">
          <div className="text-[10px] uppercase tracking-[0.12em] text-white/45 font-semibold">{it.label}</div>
          <div className="mt-1 font-outfit text-xl font-light">{it.value}</div>
        </div>
      ))}
    </div>
  );
}

export default function AdminProspects() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, raw: 0, pending_validation: 0, ready_to_send: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [scoringBatch, setScoringBatch] = useState(false);
  const [generatingBatch, setGeneratingBatch] = useState(false);
  const [enrichingBatch, setEnrichingBatch] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const fileRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (statusFilter) params.set("status", statusFilter);
        if (q.trim()) params.set("q", q.trim());
        const { data } = await adminApi.get(`/admin/prospects?${params}`);
        if (cancelled) return;
        setItems(data.items);
        setStats(data.stats);
      } catch (e) {
        if (!cancelled) toast.error("Impossible de charger les prospects.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [statusFilter, q, reloadKey]);

  const triggerReload = () => setReloadKey((k) => k + 1);

  const onDownloadTemplate = async () => {
    try {
      const res = await adminApi.get("/admin/prospects/template.csv", {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "prospects-template.csv";
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Téléchargement échoué.");
    }
  };

  const onPickFile = () => fileRef.current?.click();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await adminApi.post("/admin/prospects/import", fd);
      let msg = `${data.inserted} prospect(s) importé(s)`;
      if (data.skipped_duplicates) msg += `, ${data.skipped_duplicates} doublon(s) ignoré(s)`;
      if (data.invalid) msg += `, ${data.invalid} ligne(s) invalide(s)`;
      toast.success(msg);
      if (data.errors?.length) {
        toast.warning(`Erreurs : ${data.errors.slice(0, 3).join(" · ")}`);
      }
      triggerReload();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(typeof detail === "string" ? detail : "Erreur d'import.");
    } finally {
      setImporting(false);
    }
  };

  const onScoreBatch = async () => {
    setScoringBatch(true);
    try {
      const { data } = await adminApi.post("/admin/prospects/score-batch");
      if (data.scored === 0 && data.failed === 0) {
        toast.info(data.message || "Aucun prospect à scorer.");
      } else {
        toast.success(`${data.scored} prospect(s) scoré(s)${data.failed ? `, ${data.failed} échec(s)` : ""}.`);
      }
      if (data.errors?.length) toast.warning(data.errors.slice(0, 2).join(" · "));
      triggerReload();
    } catch (e) {
      toast.error("Scoring batch échoué.");
    } finally {
      setScoringBatch(false);
    }
  };

  const onGenerateBatch = async () => {
    if (!window.confirm("Générer audit + email pour les prospects scorés sans brouillon (max 10) ?")) return;
    setGeneratingBatch(true);
    try {
      const { data } = await adminApi.post("/admin/prospects/generate-batch");
      if (data.generated === 0 && data.failed === 0) {
        toast.info(data.message || "Aucun prospect à générer.");
      } else {
        toast.success(`${data.generated} brouillon(s) généré(s)${data.failed ? `, ${data.failed} échec(s)` : ""}.`);
      }
      if (data.errors?.length) toast.warning(data.errors.slice(0, 2).join(" · "));
      triggerReload();
    } catch (e) {
      toast.error("Génération batch échouée.");
    } finally {
      setGeneratingBatch(false);
    }
  };

  const onEnrichBatch = async () => {
    if (!window.confirm("Enrichir via Google Places les prospects sans site web (max 30) ?\nCela consomme des crédits Google Places.")) return;
    setEnrichingBatch(true);
    try {
      const { data } = await adminApi.post("/admin/prospects/enrich-batch");
      if ((data.enriched || 0) === 0 && (data.errors || 0) === 0) {
        toast.info(data.message || "Aucun prospect à enrichir.");
      } else if (data.error) {
        toast.error(data.error);
      } else {
        toast.success(`${data.enriched || 0} enrichis · ${data.no_match || 0} sans résultat · ${data.rescored || 0} re-scorés`);
      }
      triggerReload();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      toast.error(typeof detail === "string" ? detail : "Enrichissement batch échoué.");
    } finally {
      setEnrichingBatch(false);
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer définitivement ce prospect ?")) return;
    try {
      await adminApi.delete(`/admin/prospects/${id}`);
      triggerReload();
      toast.success("Prospect supprimé.");
    } catch (e) {
      toast.error("Suppression échouée.");
    }
  };

  return (
    <div className="px-6 md:px-10 py-10 max-w-7xl" data-testid="admin-prospects">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-2">
        <div>
          <div className="text-xs tracking-[2px] text-white/40 font-semibold">— PROSPECTS</div>
          <h1 className="mt-2 font-outfit text-3xl md:text-4xl font-light tracking-tight inline-flex items-center gap-3">
            <Users size={26} className="text-white/40" /> Tous les prospects
          </h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={onDownloadTemplate}
            data-testid="prospects-download-template"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition"
          >
            <Download size={14} /> Modèle CSV
          </button>
          <button
            type="button"
            onClick={onEnrichBatch}
            disabled={enrichingBatch}
            data-testid="prospects-enrich-batch"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title="Enrichir via Google Places (site, tél, note) les prospects sans site"
          >
            {enrichingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Compass size={14} />}
            {enrichingBatch ? "Enrichissement…" : "Enrichir Google (30)"}
          </button>
          <button
            type="button"
            onClick={onScoreBatch}
            disabled={scoringBatch || stats.raw === 0}
            data-testid="prospects-score-batch"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title={stats.raw === 0 ? "Aucun prospect brut à scorer" : "Lancer le scoring sur tous les bruts"}
          >
            {scoringBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap size={14} />}
            {scoringBatch ? "Scoring…" : `Scorer ${stats.raw > 0 ? `(${Math.min(25, stats.raw)})` : "bruts"}`}
          </button>
          <button
            type="button"
            onClick={onGenerateBatch}
            disabled={generatingBatch}
            data-testid="prospects-generate-batch"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 transition disabled:opacity-40 disabled:cursor-not-allowed"
            title="Générer audit + email via IA pour les prospects scorés"
          >
            {generatingBatch ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles size={14} />}
            {generatingBatch ? "Génération…" : "Générer IA (10)"}
          </button>
          <button
            type="button"
            onClick={onPickFile}
            disabled={importing}
            data-testid="prospects-import-csv"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-xs uppercase tracking-[0.12em] font-semibold gradient-bg text-white hover:shadow-[0_0_30px_rgba(229,36,49,0.4)] transition disabled:opacity-60"
          >
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload size={14} />}
            {importing ? "Import…" : "Importer CSV"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className="hidden"
            onChange={onUpload}
            data-testid="prospects-file-input"
          />
        </div>
      </div>
      <p className="text-white/55 text-sm mb-6">
        Importe une liste manuelle ou laisse la routine quotidienne remplir la base.
      </p>

      <StatsCards stats={stats} />

      {/* Filters */}
      <div className="mt-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par nom, ville, site, email…"
            data-testid="prospects-search-input"
            className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-white/30 focus:border-[#E52431]/60 outline-none transition"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          data-testid="prospects-status-filter"
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:border-[#E52431]/60 outline-none"
        >
          <option value="">Tous les statuts</option>
          <option value="raw">Brut</option>
          <option value="pending_validation">À valider</option>
          <option value="ready_to_send">Prêt à envoyer</option>
          <option value="rejected">Rejeté</option>
        </select>
      </div>

      {/* Table */}
      <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {loading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-white/40 text-sm">
            <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center" data-testid="prospects-empty">
            <FileSpreadsheet size={32} className="mx-auto text-white/20" />
            <div className="mt-3 text-sm text-white/55">Aucun prospect pour le moment.</div>
            <div className="mt-1 text-xs text-white/35">Importe une liste CSV ou attends la routine quotidienne.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="prospects-table">
              <thead className="text-[10px] uppercase tracking-[0.12em] text-white/40 border-b border-white/10">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Entreprise</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Secteur</th>
                  <th className="text-left px-4 py-3 font-semibold hidden lg:table-cell">Ville</th>
                  <th className="text-left px-4 py-3 font-semibold hidden md:table-cell">Contact</th>
                  <th className="text-left px-4 py-3 font-semibold">Score</th>
                  <th className="text-left px-4 py-3 font-semibold">Statut</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{p.nom_entreprise}</div>
                      {p.site_web && (
                        <a
                          href={p.site_web}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-[11px] text-white/50 hover:text-white mt-0.5"
                        >
                          <Globe size={10} /> {p.site_web.replace(/^https?:\/\//, "")}
                        </a>
                      )}
                    </td>
                    <td className="px-4 py-3 text-white/65 hidden md:table-cell">{p.secteur || "—"}</td>
                    <td className="px-4 py-3 text-white/65 hidden lg:table-cell">{p.ville || "—"}</td>
                    <td className="px-4 py-3 text-white/65 hidden md:table-cell">
                      {p.email_contact && (
                        <div className="inline-flex items-center gap-1 text-[12px]">
                          <Mail size={11} /> {p.email_contact}
                        </div>
                      )}
                      {p.telephone && (
                        <div className="inline-flex items-center gap-1 text-[12px] text-white/50 mt-0.5">
                          <Phone size={11} /> {p.telephone}
                        </div>
                      )}
                      {!p.email_contact && !p.telephone && "—"}
                    </td>
                    <td className="px-4 py-3">
                      {p.score == null ? (
                        <span className="text-white/40 text-xs">—</span>
                      ) : (
                        <span className={`font-semibold ${scoreClasses(p.score)}`}>{p.score}/100</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={p.status} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={() => setSelectedId(p.id)}
                          className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5 transition"
                          title="Voir détail"
                          data-testid={`prospect-view-${p.id}`}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          onClick={() => onDelete(p.id)}
                          className="p-1.5 rounded text-white/40 hover:text-[#FFB4BB] hover:bg-white/5 transition"
                          title="Supprimer"
                          data-testid={`prospect-delete-${p.id}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ProspectDetailPanel
        prospectId={selectedId}
        onClose={() => setSelectedId(null)}
        onRescored={triggerReload}
      />
    </div>
  );
}
